import React, { useState } from "react";
import { ArrowUpRight, Check, ArrowRight, Zap, Shield, Sparkles, X, Users, Building2, MapPin, TrendingUp, Award, FileSpreadsheet, Download, Info, Crown, Timer, HandCoins, Landmark, Wrench, Star } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { submitLead, API } from "@/lib/api";

const IMG = {
    hero: "/generated/franchise_hero_couple.png",
    ribbon: "/generated/franchise_ribbon_cutting.png",
    hotel: "/generated/franchise_hotel_owner.png",
    meeting: "/generated/franchise_business_meeting.png",
    multiStation: "/generated/franchise_multi_station_owner.png",
    fuelStation: "/generated/franchise_fuel_station_partnership.png",
    resort: "/generated/e3_resort_charger.png",
    highwayHub: "/generated/e3_highway_hub.png",
};

const MODELS = [
    {
        code: "FOCO",
        badge: "Founding Partner · Only 63 Slots Left",
        popular: true,
        name: "FOCO",
        fullName: "Franchise Owned, Company Operated",
        tagline: "You invest. E3 runs everything. You earn 68% cash yield.",
        capex: "₹16.5 L",
        capexNote: "Founding Partner (save ₹3.5 L)",
        royalty: "10% for lifetime",
        annualReturn: "≈ ₹11.2 L Net EBITDA",
        yield: "67.8%",
        payback: "18 months",
        image: IMG.multiStation,
        for: "HNIs · Real Estate Owners · Passive Investors",
        includes: [
            "₹16.5 L one-time CAPEX (Founding Partner rate)",
            "10% royalty on gross revenue — lifetime privilege",
            "Onboarding fee + commissioning WAIVED (save ₹3.5 L)",
            "CMS + AMC WAIVED in Year 1 (save ₹80,000)",
            "E3 handles all operations — staff, service, tech",
            "Live investor dashboard on your phone",
            "10-year lock-in · equipment ownership at end",
        ],
    },
    {
        code: "COCO",
        badge: "Zero-CAPEX Site Model",
        popular: false,
        name: "COCO",
        fullName: "Company Owned, Company Operated",
        tagline: "E3 invests. You provide the site. You earn from day one.",
        capex: "₹1 L (site prep only)",
        royalty: "88% (E3 owns the asset)",
        annualReturn: "₹4–13 L (site-dependent)",
        yield: "site + F&B uplift",
        payback: "< 1 month",
        image: IMG.hotel,
        for: "Hotels · Resorts · Fuel Stations · Landowners",
        includes: [
            "E3 funds 100% of equipment & civil (~₹16.5 L)",
            "You lease site to E3 rent-free (10 years)",
            "12% site partner revenue share, paid monthly",
            "+ 2% bonus on 95%+ uptime performance",
            "All F&B / retail footfall uplift stays with you",
            "Zero operational burden — E3 staffs and services",
            "Buyout option at book value after Year 10",
        ],
    },
];

// Each row: standard / founding side-by-side. `saving` toggles emerald highlight.
const FOCO_COSTS = [
    { section: "One-Time Investment (CAPEX)", isHeader: true },
    { label: "Franchise onboarding fee", standard: "₹ 2,00,000", founding: "WAIVED", note: "Save ₹2 L on Day 1", saving: true },
    { label: "120 kW dual-gun DC fast charger", standard: "₹ 8,00,000", founding: "₹ 8,00,000", note: "Cubenz OEM, 2-year warranty" },
    { label: "Transformer + LT panel + cabling", standard: "₹ 3,00,000", founding: "₹ 3,00,000", note: "Full electrical to charger" },
    { label: "Civil + canopy + branded signage", standard: "₹ 2,50,000", founding: "₹ 2,50,000", note: "Turnkey build by E3-approved contractor" },
    { label: "Commissioning + training + go-live", standard: "₹ 1,50,000", founding: "WAIVED", note: "Save ₹1.5 L on Day 1", saving: true },
    { label: "Working capital + soft costs", standard: "₹ 3,00,000", founding: "₹ 3,00,000", note: "DISCOM, deposits, launch marketing" },
    { label: "Total CAPEX", standard: "₹ 20,00,000", founding: "₹ 16,50,000", note: "Founding Partner saves ₹3.5 L", isTotal: true },
    { section: "Recurring — Paid to E3", isHeader: true },
    { label: "Royalty on gross charging revenue", standard: "20%", founding: "10% for lifetime", note: "Founding Partner lifetime privilege", saving: true },
    { label: "CMS + software renewal", standard: "₹ 30,000 / yr", founding: "WAIVED Year 1", note: "Year 2+: ₹30,000 / yr" },
    { label: "Annual Maintenance Contract (AMC)", standard: "₹ 50,000 / yr", founding: "WAIVED Year 1", note: "Year 2+: ₹50,000 / yr" },
    { label: "Insurance (equipment + liability)", standard: "₹ 25,000 / yr", founding: "₹ 25,000 / yr", note: "Optional but recommended" },
    { section: "Indicative Year 1 P&L (12 sessions/day base)", isHeader: true },
    { label: "Annual energy dispensed", standard: "1,97,100 kWh", founding: "1,97,100 kWh", note: "540 kWh/day × 365" },
    { label: "Gross charging revenue", standard: "₹ 35,47,800", founding: "₹ 35,47,800", note: "197,100 × ₹18/kWh" },
    { label: "Less: Royalty to E3", standard: "(₹ 7,09,560)", founding: "(₹ 3,54,780)", note: "20% standard · 10% founding", saving: true },
    { label: "Less: Energy cost @ ₹9/kWh", standard: "(₹ 17,73,900)", founding: "(₹ 17,73,900)", note: "197,100 × ₹9" },
    { label: "Less: OPEX + AMC + CMS + Insurance", standard: "(₹ 4,05,000)", founding: "(₹ 3,00,000)", note: "Founding: only OPEX in Year 1", saving: true },
    { label: "Franchisee Net EBITDA (Year 1)", standard: "₹ 6,59,340", founding: "₹ 11,19,120", note: "Founding earns ₹4.6 L more", isTotal: true, highlight: true },
    { label: "Cash yield on CAPEX", standard: "33.0%", founding: "67.8%", note: "Net EBITDA ÷ CAPEX", saving: true },
    { label: "Simple payback", standard: "36 months (3.0 yrs)", founding: "18 months (1.5 yrs)", note: "CAPEX ÷ Annual EBITDA", saving: true },
];

const FAQ = [
    { q: "How is E3 different from a franchise like ChargeZone or Statiq?", a: "ChargeZone and Statiq treat you as a location. E3 treats you as a partner — with direct OEM support from Cubenz, guaranteed revenue support in Year 1, territory exclusivity, trade-in on obsolete equipment, and lifetime Founding Partner benefits for the first 100 partners." },
    { q: "Is the ₹16.5 L investment financeable?", a: "Yes. E3 Capital Connect™ introduces qualifying partners to our banking, NBFC and green-finance network. Typical LTV 70–80% of CAPEX at competitive infra-project rates. Many Founding Partners pay only ₹3–4 L out-of-pocket." },
    { q: "What happens after the 10-year lock-in?", a: "In FOCO, you own the equipment outright at end of term. In COCO, E3 offers a buyout at book value or automatic renewal on updated commercial terms." },
    { q: "Can I switch from COCO to FOCO later?", a: "Yes. Many hotel and fuel-station partners start on COCO to prove the site, then convert to FOCO to capture full upside. E3 offers a defined conversion price at any anniversary." },
    { q: "How is uptime guaranteed?", a: "99% uptime SLA backed by Cubenz OEM. Remote monitoring 24/7, preventive maintenance quarterly, spare-part inventory at regional hubs, and priority breakdown response under 24 hours in Tier-1/2 cities." },
    { q: "Can I own multiple stations?", a: "Yes. Founding Partners get right of first refusal on adjacent territory. Multi-station owners enter the E3 Founder's Circle™ with private retreats, co-investment rights and preferential pricing." },
];

const COMPETITORS = [
    { param: "Zero-CAPEX site partner model", cz: "No", st: "No", e3: "Yes · COCO" },
    { param: "Direct OEM support", cz: "3rd party", st: "3rd party", e3: "Cubenz OEM" },
    { param: "Year 1 AMC included", cz: "Paid", st: "Paid", e3: "Included" },
    { param: "Founding Partner Program", cz: "No", st: "No", e3: "First 100 partners" },
    { param: "Territory exclusivity", cz: "Rare", st: "Rare", e3: "Standard" },
    { param: "Trade-in on old chargers", cz: "No", st: "No", e3: "Yes" },
    { param: "Fleet leads routed", cz: "Limited", st: "Moderate", e3: "High volume" },
    { param: "Year 1 revenue guarantee support", cz: "No", st: "No", e3: "Yes" },
    { param: "E3 Capital Connect (finance)", cz: "No", st: "No", e3: "Yes" },
    { param: "Founder's Circle & retreats", cz: "No", st: "No", e3: "Yes" },
];

export const Franchise = () => {
    const [selectedModel, setSelectedModel] = useState("FOCO");
    const [form, setForm] = useState({ name: "", email: "", phone: "", city: "", stations_interest: "1-2", investment_range: "16.5L-Founding", interest: "franchise", message: "" });
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await submitLead({ ...form, interest: "franchise", source_page: `franchise·${selectedModel}` });
            toast.success("Application received. The E3 partner team will reach out shortly.");
            setSubmitted(true);
            setForm({ name: "", email: "", phone: "", city: "", stations_interest: "1-2", investment_range: "16.5L-Founding", interest: "franchise", message: "" });
        } catch {
            toast.error("Something went wrong. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="franchise-page">
            {/* HERO — full-bleed with Indian couple at their E3 station */}
            <section className="relative min-h-[92vh] flex items-end overflow-hidden">
                <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${IMG.hero})` }} aria-hidden="true" />
                <div className="absolute inset-0 bg-gradient-to-t from-stone-950/90 via-stone-950/45 to-stone-950/10" aria-hidden="true" />
                <div className="relative max-w-7xl mx-auto px-6 md:px-10 pb-16 md:pb-24 w-full">
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-500/20 backdrop-blur-md border border-emerald-300/30 text-emerald-100 text-xs tracking-[0.2em] uppercase mb-8 animate-fade-up">
                        <Timer className="w-3.5 h-3.5" /> Founding Partner Program · First 100 Partners Only
                    </div>
                    <h1 data-testid="franchise-hero-title" className="font-serif text-white text-5xl md:text-7xl lg:text-8xl font-light tracking-tighter leading-[0.95] max-w-5xl animate-fade-up">
                        Own a piece of India's{" "}<br /><span className="italic e3-gradient-text">EV future.</span>
                    </h1>
                    <p className="text-white/85 text-lg md:text-xl leading-relaxed max-w-3xl mt-8 animate-fade-up">
                        Two clear models. Complete transparency. Lifetime Founding Partner benefits.
                        The first 100 partners will define the E3 network — and never share those benefits with anyone who comes later.
                    </p>
                    <div className="mt-10 flex flex-wrap gap-4 animate-fade-up">
                        <a href="#models" data-testid="franchise-cta-view-models" className="e3-btn-primary">
                            Explore FOCO & COCO <ArrowRight className="w-4 h-4" />
                        </a>
                        <a href={`${API}/franchise/costsheet.xlsx`} data-testid="franchise-download-costsheet-hero" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold text-sm text-white bg-stone-950/60 backdrop-blur-lg border border-white/50 hover:bg-stone-950/80 shadow-xl transition">
                            <FileSpreadsheet className="w-4 h-4" /> Download Full Cost Sheet
                        </a>
                    </div>
                </div>
            </section>

            {/* SCARCITY BAND */}
            <section className="bg-stone-950 text-white py-6 px-6 md:px-10 border-y border-stone-800">
                <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4 text-sm">
                    <div className="flex items-center gap-3 text-emerald-300">
                        <Crown className="w-5 h-5" />
                        <span className="font-semibold tracking-wide">Founding Partner Slots Remaining</span>
                    </div>
                    <div className="flex items-center gap-6">
                        <div className="text-center">
                            <div className="font-serif text-3xl e3-gradient-text">63</div>
                            <div className="text-[10px] text-stone-500 tracking-widest uppercase">of 100</div>
                        </div>
                        <div className="hidden md:block h-8 w-px bg-stone-800" />
                        <div className="text-stone-400">Lifetime preferential pricing · Territory rights · Founder's Circle access</div>
                    </div>
                </div>
            </section>

            {/* TWO MODELS */}
            <section id="models" className="py-24 md:py-32 px-6 md:px-10 scroll-mt-20">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center max-w-3xl mx-auto mb-16">
                        <div className="e3-overline mb-6">Two Simple Models</div>
                        <h2 className="font-serif text-4xl md:text-6xl font-light tracking-tighter leading-[1.05] text-stone-900">
                            Pick your path. <span className="italic e3-gradient-text">FOCO or COCO.</span>
                        </h2>
                        <p className="text-stone-600 text-lg leading-relaxed mt-6">
                            You either have capital and want passive infrastructure income — or you have a great site
                            and want zero-CAPEX EV upside. E3 gives you exactly one of those.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {MODELS.map((m) => (
                            <div
                                key={m.code}
                                data-testid={`franchise-model-${m.code}`}
                                className={`relative rounded-3xl overflow-hidden border transition-all ${
                                    m.popular
                                        ? "bg-stone-950 text-white border-stone-950 shadow-2xl md:scale-[1.02]"
                                        : "bg-white text-stone-900 border-stone-200 hover:border-stone-400"
                                }`}
                            >
                                <div className="aspect-[16/10] overflow-hidden bg-stone-100">
                                    <img src={m.image} alt={m.name} className="w-full h-full object-cover" />
                                </div>
                                <div className="p-8 md:p-10">
                                    <div className="flex items-start justify-between mb-4">
                                        <div>
                                            <div className={`text-xs tracking-[0.2em] uppercase mb-2 ${m.popular ? "text-emerald-300" : "text-stone-500"}`}>{m.badge}</div>
                                            <div className={`font-serif text-4xl font-medium ${m.popular ? "text-white" : "text-stone-900"}`}>{m.name}</div>
                                            <div className={`text-sm mt-1 ${m.popular ? "text-stone-400" : "text-stone-500"}`}>{m.fullName}</div>
                                        </div>
                                        {m.popular && (
                                            <span className="px-3 py-1 rounded-full bg-emerald-400 text-stone-900 text-xs font-bold uppercase tracking-wider">Popular</span>
                                        )}
                                    </div>

                                    <p className={`text-lg font-medium mt-4 mb-6 ${m.popular ? "e3-gradient-text" : "text-stone-900"}`}>
                                        {m.tagline}
                                    </p>

                                    <div className={`grid grid-cols-2 gap-3 py-6 border-y ${m.popular ? "border-stone-800" : "border-stone-200"}`}>
                                        <MetricRow label="Your CAPEX" value={m.capex} dark={m.popular} />
                                        <MetricRow label="To E3" value={m.royalty} dark={m.popular} />
                                        <MetricRow label="Year 1 Return" value={m.annualReturn} dark={m.popular} highlight />
                                        <MetricRow label={m.code === "FOCO" ? "Payback" : "Payback"} value={m.payback} dark={m.popular} highlight={m.code === "FOCO"} />
                                    </div>

                                    <div className={`text-xs uppercase tracking-widest mt-6 mb-4 ${m.popular ? "text-stone-400" : "text-stone-500"}`}>
                                        Ideal for
                                    </div>
                                    <div className={`text-sm mb-6 ${m.popular ? "text-stone-300" : "text-stone-700"}`}>{m.for}</div>

                                    <ul className="space-y-2.5 mb-8">
                                        {m.includes.map((i) => (
                                            <li key={i} className={`flex items-start gap-2.5 text-sm ${m.popular ? "text-stone-300" : "text-stone-700"}`}>
                                                <Check className={`w-4 h-4 mt-0.5 shrink-0 ${m.popular ? "text-emerald-400" : "text-emerald-600"}`} />
                                                {i}
                                            </li>
                                        ))}
                                    </ul>

                                    <button
                                        onClick={() => { setSelectedModel(m.code); document.getElementById("franchise-form")?.scrollIntoView({ behavior: "smooth" }); }}
                                        data-testid={`franchise-model-cta-${m.code}`}
                                        className={`w-full rounded-full py-3 font-semibold flex items-center justify-center gap-2 transition ${
                                            m.popular
                                                ? "bg-white text-stone-900 hover:bg-stone-100"
                                                : "e3-gradient-bg text-white hover:brightness-110"
                                        }`}
                                    >
                                        Apply for {m.name} <ArrowRight className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* DETAILED COST SHEET — FOCO */}
            <section className="py-24 md:py-32 px-6 md:px-10 bg-stone-50">
                <div className="max-w-7xl mx-auto">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end mb-10">
                        <div className="md:col-span-7">
                            <div className="e3-overline mb-6">FOCO · Complete Cost Sheet</div>
                            <h2 className="font-serif text-4xl md:text-6xl font-light tracking-tighter leading-[1.05] text-stone-900">
                                Every rupee, <span className="italic e3-gradient-text">transparent.</span>
                            </h2>
                        </div>
                        <div className="md:col-span-5">
                            <p className="text-stone-600 text-lg leading-relaxed">
                                Standard vs Founding Partner — side by side. Founding Partners pay <strong className="text-stone-900">₹3.5 L less on Day 1</strong>, lock in <strong className="text-stone-900">10% royalty for lifetime</strong>, and get first Year recurring costs waived.
                            </p>
                        </div>
                    </div>

                    {/* Founding Partner Discount Banner */}
                    <div className="mb-8 rounded-2xl e3-gradient-bg text-white p-6 md:p-8 relative overflow-hidden" data-testid="franchise-founding-banner">
                        <div className="absolute -top-32 -right-32 w-80 h-80 bg-white/10 rounded-full blur-3xl" />
                        <div className="relative grid grid-cols-1 md:grid-cols-4 gap-6">
                            <div className="md:col-span-2">
                                <div className="flex items-center gap-2 mb-3">
                                    <Crown className="w-5 h-5 text-amber-300" />
                                    <span className="text-xs tracking-[0.2em] uppercase font-bold text-amber-200">Founding Partner Bonus · First 100 Only</span>
                                </div>
                                <div className="font-serif text-3xl md:text-4xl font-light leading-tight mb-2">
                                    Save ₹3.5 L on Day 1<br />+ 10% royalty for lifetime
                                </div>
                                <div className="text-white/80 text-sm">
                                    Onboarding fee, commissioning, Year 1 CMS & AMC — all waived.
                                </div>
                            </div>
                            <FoundingStat label="CAPEX" value="₹ 16.5 L" strike="₹ 20 L" />
                            <FoundingStat label="Payback" value="18 months" strike="36 months" />
                        </div>
                    </div>

                    <div className="rounded-2xl overflow-hidden border border-stone-200 bg-white shadow-sm" data-testid="franchise-cost-sheet">
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-stone-100 hover:bg-stone-100">
                                    <TableHead className="text-stone-700 font-semibold py-5">Line item</TableHead>
                                    <TableHead className="text-stone-500 font-semibold text-right">Standard Partner</TableHead>
                                    <TableHead className="text-emerald-800 font-semibold text-right">
                                        <div className="flex items-center justify-end gap-1.5">
                                            <Crown className="w-3.5 h-3.5" /> Founding Partner
                                        </div>
                                    </TableHead>
                                    <TableHead className="text-stone-700 font-semibold">Notes</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {FOCO_COSTS.map((row, i) => {
                                    if (row.isHeader) {
                                        return (
                                            <TableRow key={i} className="bg-stone-50/60 hover:bg-stone-50/60 border-stone-200">
                                                <TableCell colSpan={4} className="e3-overline py-4 text-emerald-800">{row.section}</TableCell>
                                            </TableRow>
                                        );
                                    }
                                    return (
                                        <TableRow key={i} className={`border-stone-200 ${row.highlight ? "bg-emerald-50 border-emerald-200" : ""}`}>
                                            <TableCell className={`py-4 ${row.isTotal ? "font-semibold text-stone-900" : "text-stone-800"}`}>{row.label}</TableCell>
                                            <TableCell className={`text-right tabular-nums text-stone-500 ${row.isTotal ? "font-semibold" : ""} ${row.saving ? "line-through decoration-stone-300" : ""}`}>{row.standard}</TableCell>
                                            <TableCell className={`text-right tabular-nums ${row.isTotal ? (row.highlight ? "font-bold e3-gradient-text text-lg" : "font-semibold text-emerald-800") : "text-emerald-800 font-semibold"} ${row.saving && !row.isTotal ? "bg-amber-50" : ""}`}>{row.founding}</TableCell>
                                            <TableCell className="text-xs text-stone-500 max-w-md">{row.note}</TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </div>

                    <details className="mt-8 text-sm text-stone-500 leading-relaxed max-w-4xl">
                        <summary className="cursor-pointer font-semibold text-stone-700 mb-2 flex items-center gap-2 hover:text-stone-900">
                            <Info className="w-3.5 h-3.5" /> Read the small print (assumptions & terms)
                        </summary>
                        <div className="mt-3 space-y-2 text-xs">
                            <p><strong>Base case utilisation:</strong> 12 charging sessions per day × 45 kWh per session = 540 kWh/day, 197,100 kWh/year. Achieving higher utilisation (14-18 sessions) is common on highway and hotel corridors and materially improves EBITDA.</p>
                            <p><strong>Tariff assumption ₹18/kWh:</strong> Blended B2C + fleet-contract rate. Fleet contracts (BluSmart, Ola, Uber Green, corporate fleets) routed by E3 typically at ₹14-17. Retail B2C at ₹22-26. Blended.</p>
                            <p><strong>Founding Partner Royalty (10% lifetime):</strong> First 100 partners lock in a permanent 10% royalty vs 20% for later partners. Applied on gross charging revenue, deducted automatically at settlement. This lifetime privilege alone is worth ₹3.5 L per year per station.</p>
                            <p><strong>Founding Partner CAPEX savings (₹3.5 L):</strong> Onboarding fee (₹2 L) and Commissioning + training (₹1.5 L) fully waived for the first 100 partners.</p>
                            <p><strong>Year 1 recurring waived:</strong> CMS renewal (₹30,000) and AMC (₹50,000) waived for Founding Partners in Year 1 only. From Year 2 onwards, standard rates apply with 5% annual indexation.</p>
                            <p><strong>Lock-in:</strong> 10 years. Equipment ownership transfers cleanly to franchisee at end of term. Early exit permitted with 90-day notice and 10% termination fee on remaining lock-in months.</p>
                            <p><strong>Financing:</strong> ₹16.5 L CAPEX financeable through E3 Capital Connect™ partner network. Typical LTV 70-80% at infrastructure project rates — many Founding Partners pay just ₹3-4 L equity.</p>
                        </div>
                    </details>

                    <div className="mt-8 rounded-2xl border-2 border-emerald-200 bg-emerald-50/60 p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6" data-testid="franchise-excel-cta-band">
                        <div className="flex items-start gap-4">
                            <div className="w-12 h-12 rounded-xl e3-gradient-bg flex items-center justify-center shrink-0 text-white">
                                <FileSpreadsheet className="w-6 h-6" />
                            </div>
                            <div>
                                <div className="font-serif text-xl md:text-2xl font-medium text-stone-900 mb-1">Full 6-sheet cost workbook</div>
                                <div className="text-stone-600 text-sm">FOCO · COCO · Model comparison · Portfolio growth · Vs ChargeZone/Statiq · Disclaimer</div>
                            </div>
                        </div>
                        <a href={`${API}/franchise/costsheet.xlsx`} data-testid="franchise-download-costsheet" className="e3-btn-primary shrink-0">
                            <Download className="w-4 h-4" /> Download .xlsx
                        </a>
                    </div>
                </div>
            </section>

            {/* WHAT YOU GET FROM E3 */}
            <section className="py-24 md:py-32 px-6 md:px-10">
                <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
                    <div className="md:col-span-6">
                        <div className="rounded-2xl overflow-hidden aspect-[4/3] shadow-xl">
                            <img src={IMG.meeting} alt="Business partnership meeting" className="w-full h-full object-cover" />
                        </div>
                    </div>
                    <div className="md:col-span-6">
                        <div className="e3-overline mb-6">What E3 Provides</div>
                        <h2 className="font-serif text-4xl md:text-5xl font-light tracking-tighter leading-[1.05] text-stone-900 mb-8">
                            You focus on the site. <span className="italic e3-gradient-text">We handle everything else.</span>
                        </h2>
                        <div className="space-y-3">
                            {[
                                { icon: Shield, t: "Brand & Marketing", d: "E3 CHARGE NETWORK brand, digital marketing, PR, launch campaigns" },
                                { icon: Zap, t: "Cubenz OEM Equipment", d: "120 kW dual-gun DC fast charger, 2-year warranty, upgrade path" },
                                { icon: Wrench, t: "Installation & Commissioning", d: "Turnkey site build, DISCOM coordination, team training" },
                                { icon: Sparkles, t: "Technology Stack", d: "CMS, mobile app, live dashboards, OCPP, session settlement" },
                                { icon: Users, t: "Fleet & Business Development", d: "Central BD desk routes fleet, corporate & app-based demand to your station" },
                                { icon: Landmark, t: "E3 Capital Connect™", d: "Introductions to banks, NBFCs, green-finance partners" },
                            ].map((f) => {
                                const Icon = f.icon;
                                return (
                                    <div key={f.t} className="flex items-start gap-4 py-3 border-b border-stone-200">
                                        <div className="w-9 h-9 rounded-lg e3-gradient-soft flex items-center justify-center shrink-0">
                                            <Icon className="w-4 h-4 text-emerald-700" strokeWidth={1.75} />
                                        </div>
                                        <div>
                                            <div className="font-semibold text-stone-900">{f.t}</div>
                                            <div className="text-sm text-stone-600">{f.d}</div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </section>

            {/* COMPETITION COMPARISON */}
            <section className="py-24 md:py-32 px-6 md:px-10 bg-stone-950 text-white">
                <div className="max-w-7xl mx-auto">
                    <div className="e3-overline mb-6 text-stone-400">Beat the competition</div>
                    <h2 className="font-serif text-4xl md:text-6xl font-light tracking-tighter leading-[1.05] mb-6 max-w-4xl">
                        Ten reasons E3 franchise beats <span className="italic e3-gradient-text">ChargeZone & Statiq.</span>
                    </h2>
                    <p className="text-stone-400 text-lg leading-relaxed mb-14 max-w-2xl">
                        We don't sell a machine. We sell a decade-long partnership designed to make you win.
                    </p>

                    <div className="rounded-2xl overflow-hidden border border-stone-800 bg-stone-900/50" data-testid="franchise-competitor-table">
                        <Table>
                            <TableHeader>
                                <TableRow className="border-stone-800 hover:bg-stone-900">
                                    <TableHead className="text-stone-400 text-xs tracking-widest uppercase py-5">Parameter</TableHead>
                                    <TableHead className="text-stone-500 text-xs tracking-widest uppercase text-center">ChargeZone</TableHead>
                                    <TableHead className="text-stone-500 text-xs tracking-widest uppercase text-center">Statiq</TableHead>
                                    <TableHead className="e3-gradient-text font-bold text-sm tracking-widest uppercase text-center">E3</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {COMPETITORS.map((row, i) => (
                                    <TableRow key={i} className="border-stone-800 hover:bg-stone-800/40">
                                        <TableCell className="text-white py-4 font-medium">{row.param}</TableCell>
                                        <TableCell className="text-stone-500 text-center">
                                            <span className="inline-flex items-center gap-1"><X className="w-3.5 h-3.5" /> {row.cz}</span>
                                        </TableCell>
                                        <TableCell className="text-stone-500 text-center">{row.st}</TableCell>
                                        <TableCell className="text-emerald-400 text-center font-semibold bg-emerald-900/20">
                                            <span className="inline-flex items-center gap-1"><Check className="w-3.5 h-3.5" /> {row.e3}</span>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                </div>
            </section>

            {/* MILESTONE MOMENT */}
            <section className="relative min-h-[60vh] flex items-end overflow-hidden">
                <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${IMG.ribbon})` }} aria-hidden="true" />
                <div className="absolute inset-0 bg-gradient-to-t from-stone-950/85 via-stone-950/30 to-transparent" aria-hidden="true" />
                <div className="relative max-w-7xl mx-auto px-6 md:px-10 pb-16 md:pb-24 w-full">
                    <div className="e3-overline mb-4 text-emerald-300">Ribbon-cutting · Somewhere in India</div>
                    <h2 className="font-serif text-white text-4xl md:text-6xl font-light tracking-tighter leading-[1.05] max-w-4xl">
                        Your grand opening is <span className="italic e3-gradient-text">6 months away.</span>
                    </h2>
                    <p className="text-white/80 text-lg mt-6 max-w-2xl">
                        From application to go-live in 90–180 days.
                    </p>
                </div>
            </section>

            {/* FAQ */}
            <section className="py-24 md:py-32 px-6 md:px-10">
                <div className="max-w-4xl mx-auto">
                    <div className="e3-overline mb-6">Frequently Asked</div>
                    <h2 className="font-serif text-4xl md:text-5xl font-light tracking-tighter leading-[1.05] text-stone-900 mb-14">
                        Answers to what you're actually thinking.
                    </h2>
                    <div className="space-y-3" data-testid="franchise-faq">
                        {FAQ.map((f, i) => (
                            <details key={i} className="group rounded-2xl border border-stone-200 bg-white p-6 hover:border-stone-400 transition" data-testid={`franchise-faq-${i}`}>
                                <summary className="cursor-pointer flex items-center justify-between gap-4 font-serif text-lg text-stone-900 font-medium list-none">
                                    <span>{f.q}</span>
                                    <span className="w-8 h-8 rounded-full border border-stone-300 flex items-center justify-center shrink-0 group-open:e3-gradient-bg group-open:text-white group-open:border-transparent transition">
                                        <ArrowRight className="w-4 h-4 group-open:rotate-90 transition-transform" />
                                    </span>
                                </summary>
                                <p className="mt-4 text-stone-600 leading-relaxed">{f.a}</p>
                            </details>
                        ))}
                    </div>
                </div>
            </section>

            {/* APPLICATION FORM */}
            <section id="franchise-form" className="py-24 md:py-32 px-6 md:px-10 scroll-mt-20 bg-stone-50">
                <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-12">
                    <div className="md:col-span-5">
                        <div className="e3-overline mb-6">Apply Now</div>
                        <h2 className="font-serif text-4xl md:text-5xl font-light tracking-tighter leading-[1.05] text-stone-900 mb-6">
                            Reserve your <span className="italic e3-gradient-text">Founding Partner slot.</span>
                        </h2>
                        <p className="text-stone-600 text-lg leading-relaxed mb-8">
                            Only 63 of 100 Founding Partner slots remain. Once filled, lifetime preferential pricing and territory rights close forever.
                        </p>
                        <div className="rounded-2xl border border-stone-200 bg-white p-6">
                            <div className="e3-overline mb-3">What happens next</div>
                            <ol className="space-y-3 text-sm text-stone-700">
                                {["Application received", "E3 team calls within 48 hours", "Site assessment scheduled", "Commercial proposal sent", "Franchise agreement signed"].map((s, i) => (
                                    <li key={s} className="flex items-start gap-3">
                                        <span className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                                        {s}
                                    </li>
                                ))}
                            </ol>
                        </div>
                    </div>
                    <div className="md:col-span-7">
                        {submitted ? (
                            <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-10" data-testid="franchise-form-success">
                                <div className="font-serif text-3xl text-stone-900 mb-3">Application received.</div>
                                <p className="text-stone-700 mb-6">The E3 partner team will reach out within 48 hours.</p>
                                <button onClick={() => setSubmitted(false)} className="e3-btn-ghost">Submit another</button>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="rounded-2xl bg-white border border-stone-200 p-8 md:p-10 space-y-5" data-testid="franchise-form">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <FormField label="Full Name" required>
                                        <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" data-testid="franchise-form-name" />
                                    </FormField>
                                    <FormField label="Email" required>
                                        <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@company.com" data-testid="franchise-form-email" />
                                    </FormField>
                                    <FormField label="Phone" required>
                                        <Input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91 ·············" data-testid="franchise-form-phone" />
                                    </FormField>
                                    <FormField label="City / State">
                                        <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="e.g. Ahmedabad, Gujarat" data-testid="franchise-form-city" />
                                    </FormField>
                                    <FormField label="Preferred Model">
                                        <Select value={selectedModel} onValueChange={setSelectedModel}>
                                            <SelectTrigger data-testid="franchise-form-model"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="FOCO">FOCO — I want to invest ₹16.5 L (Founding Partner)</SelectItem>
                                                <SelectItem value="COCO">COCO — I have a great site</SelectItem>
                                                <SelectItem value="Both">Not sure — recommend for me</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </FormField>
                                    <FormField label="Stations of Interest">
                                        <Select value={form.stations_interest} onValueChange={(v) => setForm({ ...form, stations_interest: v })}>
                                            <SelectTrigger data-testid="franchise-form-stations"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="1-2">1–2 stations</SelectItem>
                                                <SelectItem value="3-5">3–5 stations</SelectItem>
                                                <SelectItem value="5-10">5–10 stations</SelectItem>
                                                <SelectItem value="10+">10+ stations</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </FormField>
                                </div>
                                <FormField label="Tell us about your site or plans (optional)">
                                    <Textarea rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Location type, land size, timeline..." data-testid="franchise-form-message" />
                                </FormField>
                                <button type="submit" disabled={submitting} className="e3-btn-primary w-full justify-center" data-testid="franchise-form-submit">
                                    {submitting ? "Submitting…" : "Reserve My Slot"} <ArrowUpRight className="w-4 h-4" />
                                </button>
                                <p className="text-xs text-stone-500 text-center">By submitting, you agree to be contacted by the E3 partner team. No spam — one call within 48 hours.</p>
                            </form>
                        )}
                    </div>
                </div>
            </section>
        </div>
    );
};

const MetricRow = ({ label, value, dark, highlight }) => (
    <div>
        <div className={`text-[0.65rem] uppercase tracking-widest mb-1 ${dark ? "text-stone-400" : "text-stone-500"}`}>{label}</div>
        <div className={`font-serif text-xl leading-tight ${highlight ? "e3-gradient-text font-semibold" : (dark ? "text-white" : "text-stone-900")}`}>{value}</div>
    </div>
);

const FoundingStat = ({ label, value, strike }) => (
    <div className="border-l border-white/20 pl-6">
        <div className="text-[0.65rem] uppercase tracking-widest text-white/60 mb-2">{label}</div>
        {strike && <div className="text-sm text-white/50 line-through mb-1">{strike}</div>}
        <div className="font-serif text-3xl md:text-4xl font-light text-white leading-none">{value}</div>
    </div>
);

const FormField = ({ label, required, children }) => (
    <div>
        <label className="block text-xs font-semibold text-stone-600 uppercase tracking-wider mb-2">
            {label} {required && <span className="text-emerald-600">*</span>}
        </label>
        {children}
    </div>
);
